import StarIcon from '@assets/icons/star.svg?react'
import StarFilledIcon from '@assets/icons/star-filled.svg?react'
import { MMButton } from '@components'
import { showToast } from '@utils'
import cn from 'classnames'
import { useSetAccountFavoriteMutation } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'
import { type BaseSyntheticEvent, useEffect, useState } from 'react'
import styles from './FavoriteButton.module.scss'

interface FavoriteButtonProps {
  accountId: number
  isFavorite?: boolean
  className?: string
}

export const FavoriteButton = ({ accountId, isFavorite: savedValue = false, className }: FavoriteButtonProps) => {
  const [setFavorite, { isLoading }] = useSetAccountFavoriteMutation()

  // Optimistic value, held until the refetched list confirms it (or the request fails), so the
  // star reacts instantly instead of waiting for the PATCH and the list reload.
  const [pending, setPending] = useState<boolean | null>(null)
  const isFavorite = pending ?? savedValue

  useEffect(() => {
    if (pending !== null && pending === savedValue) setPending(null)
  }, [pending, savedValue])

  const label = isFavorite
    ? tr('FRONTEND.ACCOUNTS.FAVORITE.REMOVE', 'Remove from favorites')
    : tr('FRONTEND.ACCOUNTS.FAVORITE.ADD', 'Add to favorites')

  const handleClick = (e: BaseSyntheticEvent) => {
    // the button lives inside clickable rows/cards
    e.stopPropagation()
    if (isLoading) return
    const next = !isFavorite
    setPending(next)
    setFavorite({ accountId, isFavorite: next })
      .unwrap()
      .catch(() => {
        setPending(null)
        showToast('error', tr('FRONTEND.ACCOUNTS.FAVORITE.ERROR', 'Could not update favorites. Please try again.'))
      })
  }

  return (
    <MMButton
      type='button'
      size='small'
      variant='tertiary'
      collapsed
      collapsedIcon={isFavorite ? StarFilledIcon : StarIcon}
      title={label}
      aria-pressed={isFavorite}
      className={cn(styles['favorite-button'], 'favorite-button', { 'favorite-button-active': isFavorite }, className)}
      onClick={handleClick}
      data-test={`account-favorite-${accountId}`}
    />
  )
}
